import { CommonModule } from "@angular/common";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { MonitoringService } from "./services/monitoring.service";
import { BarController, BarElement, CategoryScale, Chart, Filler, Legend, LinearScale, LineController, LineElement, LogarithmicScale, PointElement, Title, Tooltip } from "chart.js";
import { DevicesDto } from "@shared/models/devices-dto";
import { NgSelectComponent } from "@ng-select/ng-select";
import { LocalStorageService } from "@shared/services/utilities/local-storage.service";
import { Router } from "@angular/router";
import { catchError, exhaustMap, forkJoin, interval, map, startWith, Subscription, of } from "rxjs";
import { MENU_OPTIONS } from "@shared/constants/left-menu-option";
import { Site } from "@shared/models/site";
import { ControlPoint } from "@shared/models/controlPoint";
import { ControlPointsService } from "@features/control-points/services/control-points-service";

interface MonitoringFilters {
  selectedDeviceIds?: number[];
  selectedDate?: string;
  selectedDateString?: string;
  startTime?: string;
  endTime?: string;
  selectedTimeRange?: string;
  selectedSiteId?: number;
}

@Component({
    standalone: true,
    selector: 'app-monitoring',
    templateUrl: './monitoring.component.html',
    styleUrls: ['./monitoring.component.scss'],
    imports: [CommonModule, FormsModule,MatIconModule, TranslateModule,AngularSvgIconModule,NgSelectComponent,
        ReactiveFormsModule
    ]
})
export class MonitoringComponent implements OnInit {
    @ViewChild('scrollContainer') scrollContainer!: ElementRef;
    devices: DevicesDto[] = [];
    currentSiteId: number | undefined;
    disabled: boolean = false;
    private pollingSubscriptions = new Map<number, Subscription>();
    isDetailsVisible: boolean[] = [];
    isChannelsVisible: boolean[] = []; // Track visibility of channels for each device
    selectedDeviceIndex: number = 0;
    menuOptions = MENU_OPTIONS;
    private charts: { [index: number]: Chart } = {};
    private channelCharts: { [index: number]: Chart } = {}; // Un seul chart combiné par device
    private chartInitialized: { [index: number]: boolean } = {};
    private channelChartInitialized: { [index: number]: { [channel: number]: boolean } } = {};
    private maxDataPoints = 10000; // Nombre maximum de points affichés
    redAlarmReachedDevices: number[] = [];
    orangeAlarmReachedDevices: number[] = [];
    yellowAlarmReachedDevices: number[] = [];
    selectedDeviceIds: number[] = [];
    selectedSiteId: number | undefined;
    protected listeSites: Site[] = [];
    controlPoints: ControlPoint[] = [];
    selectedDate: Date  = new Date();
    selectedDateString: string;
    startTime: string = '00:00:00';
    endTime: string = '23:59:59';
    selectedTimeRange: string = 'custom';
    timeRangeOptions: { value: string, label: string }[] = [];
    initialDeviceList: DevicesDto[] = [];
    private isLoading = false;
    private currentPage = 1;
    private totalPages = 1;
    private pageSize = 10;
    today: Date = new Date();
    private readonly appTimeZone = 'Europe/Paris';
    maxDate: string;
    private deviceDataLengthMap: Map<number, number> = new Map();
    deviceMaxValues: { maxVecSum: number | null, maxChannels: number | null, maxSound: number | null }[] = [];
    constructor(private monitoringService: MonitoringService, private el: ElementRef, private localStorageService: LocalStorageService, 
        private router: Router, private controlPointsHttpService: ControlPointsService,
        private translateService: TranslateService
    ) {  
          Chart.register(
              CategoryScale,
              LinearScale,
              LogarithmicScale,
              Title,
              Tooltip,
              Legend,
              LineController,
              LineElement,
              PointElement,
              BarController,
              BarElement,
              Filler,
            );
    }
    ngAfterViewInit() {
        this.scrollContainer.nativeElement.addEventListener('scroll', this.onScroll.bind(this));

        this.initializeTimeRangeOptions();
    }
    ngOnInit(): void {
        this.currentSiteId = this.localStorageService.getItem('currentSiteID');
        const appNow = this.getNowInAppTimeZone();
        this.today = new Date(appNow.getFullYear(), appNow.getMonth(), appNow.getDate());
        const year = this.today.getFullYear();
        const month = String(this.today.getMonth() + 1).padStart(2, '0');
        const day = String(this.today.getDate()).padStart(2, '0');
        this.maxDate = `${year}-${month}-${day}`;
         this.getDevicesNames();
        this.listeSites = this.localStorageService.getItem('allSites') || [];
        if (this.selectedDeviceIndex >= 0) {
            this.isDetailsVisible[this.selectedDeviceIndex] = true;
          }
        this.loadSavedFilters();
        
        this.getDevices();
        
        setTimeout(() => {
            this.initializeTimeRangeOptions();
        }, 0);
        this.translateService.onLangChange.subscribe(() => {
            this.initializeTimeRangeOptions();
        });
    }
    
    private initializeTimeRangeOptions(): void {
        this.timeRangeOptions = [
            { value: 'custom', label: this.translateService.instant('MONITORING.TIME_RANGE.CUSTOM') },
            { value: 'today', label: this.translateService.instant('MONITORING.TIME_RANGE.TODAY') },
            { value: 'last30min', label: this.translateService.instant('MONITORING.TIME_RANGE.LAST_30_MIN') },
            { value: 'last1hour', label: this.translateService.instant('MONITORING.TIME_RANGE.LAST_1_HOUR') },
            { value: 'last2hours', label: this.translateService.instant('MONITORING.TIME_RANGE.LAST_2_HOURS') },
            { value: 'last4hours', label: this.translateService.instant('MONITORING.TIME_RANGE.LAST_4_HOURS') }
        ];
    }
    
        private formatDate(date: Date): string {
            const year = date.getFullYear();
            const month = ('0' + (date.getMonth() + 1)).slice(-2);
            const day = ('0' + date.getDate()).slice(-2);
            return `${year}-${month}-${day}`;
        }
        getDevicesNames(): void {
            this.monitoringService.getMonitoringDeviceNames().subscribe( x => {
                this.initialDeviceList = x;
            });
        }

getDevices(pageNumber: number = 1, append: boolean = false): void {
  if (this.isLoading) return;
  this.isLoading = true;
  const effectiveSiteId = this.selectedSiteId && this.selectedSiteId > 0 ? this.selectedSiteId : this.currentSiteId;

  const searchCriteria: any = {
    pageNumber: pageNumber,
    pageSize: this.pageSize,
    devicesIds: this.selectedDeviceIds ?? null,
  };
  if (this.selectedSiteId != null) {
    searchCriteria.siteId = this.selectedSiteId;
  }

  forkJoin({
    controlPointsResponse: this.controlPointsHttpService.getControlPointsBySiteId(effectiveSiteId, true),
    response: this.monitoringService.getMonitoringDevices(searchCriteria)
  })
    .pipe(
      catchError(error => {
        this.isLoading = false;
        console.error('Erreur lors du chargement des control points ou devices', error);
        throw error;
      })
    )
    .subscribe({
      next: ({ response, controlPointsResponse }) => {
        this.controlPoints = controlPointsResponse;
        if (append) {
          this.devices = [...this.devices, ...response.data];
          this.isDetailsVisible = [
            ...this.isDetailsVisible,
            ...new Array(response.data.length).fill(false)
          ];
        } else {
          this.devices = response.data;
          this.isDetailsVisible = new Array(this.devices.length).fill(false);
          if (this.devices.length > 0) {
            this.isDetailsVisible[0] = true;
            this.selectedDeviceIndex = 0;
            this.deviceDataLengthMap.clear();
            this.loadSignalData(0);
          }
        }
        this.totalPages = response.totalPages || this.totalPages;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
}
  onScroll(event: Event): void {
    const element = this.scrollContainer.nativeElement;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 100;
    if (atBottom && !this.isLoading ) {
      this.currentPage++;
      this.getDevices(this.currentPage, true);
    }
  }
  
    private calculateTimeRange(timeRange: string): { startDate: Date, endDate: Date } {
        const now = this.getNowInAppTimeZone();
    let startDate: Date;
    let endDate: Date = new Date(now);
    
    switch (timeRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        break;
      case 'last30min':
        startDate = new Date(now.getTime() - 30 * 60 * 1000);
        break;
      case 'last1hour':
        startDate = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case 'last2hours':
        startDate = new Date(now.getTime() - 2 * 60 * 60 * 1000);
        break;
      case 'last4hours':
        startDate = new Date(now.getTime() - 4 * 60 * 60 * 1000);
        break;
      default:
        const year = this.selectedDate.getFullYear();
        const month = this.selectedDate.getMonth();
        const day = this.selectedDate.getDate();
        
        const [startHour, startMinute, startSecond = '0'] = this.startTime.split(':');
        startDate = new Date(year, month, day, parseInt(startHour), parseInt(startMinute), parseInt(startSecond), 0);
        
        const [endHour, endMinute, endSecond = '59'] = this.endTime.split(':');
        endDate = new Date(year, month, day, parseInt(endHour), parseInt(endMinute), parseInt(endSecond), 999);
        break;
    }
    
    return { startDate, endDate };
  }

    private getNowInAppTimeZone(): Date {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: this.appTimeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23'
        }).formatToParts(new Date());

        const value = (type: string) => Number(parts.find(part => part.type === type)?.value ?? 0);
        return new Date(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'), value('second'), 0);
    }

    /**
     * Formate une Date en local time string (YYYY-MM-DD HH:mm:ss)
     * pour envoyer au backend sans conversion UTC.
     */
    /**
     * Formate une Date en local time string ISO (YYYY-MM-DDTHH:mm:ss)
     * pour envoyer au backend sans conversion UTC.
     */
    private formatLocalDateTime(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }

    private parseBackendLocalDate(value: any): Date | null {
        if (value == null) return null;
        const ts = String(value).trim();
        // Parse local time string: accepts both "YYYY-MM-DDTHH:mm:ss" and "YYYY-MM-DD HH:mm:ss" formats
        // with optional milliseconds (.SSS)
        const dateRegex = /(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?/;
        const match = ts.match(dateRegex);
        if (match) {
            const [, year, month, day, hours, minutes, seconds, ms] = match;
            const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes), parseInt(seconds), parseInt(ms || '0'));
            return isNaN(d.getTime()) ? null : d;
        }
        return null;
    }

    private getBackendLocalTimestampMs(value: any): number {
        const d = this.parseBackendLocalDate(value);
        return d ? d.getTime() : 0;
    }
  
    private currentPopup: HTMLElement | null = null;
    private currentRectangleIndex: number | null = null;

    private removeCurrentPopup(): void {
        if (this.currentPopup) {
            this.currentPopup.remove();
            this.currentPopup = null;
        }
        this.currentRectangleIndex = null;
    }
    
    private createContinuousTimeSeries(sortedMonitorings: any[], rangeStart?: Date, rangeEnd?: Date): { timestamps: string[], values: number[], monitoringMap: Map<number, any> } {
        if (sortedMonitorings.length === 0 && !rangeStart) {
            return { timestamps: [], values: [], monitoringMap: new Map() };
        }
        
        const firstMonitoringDate = this.parseBackendLocalDate(sortedMonitorings[0].timestamp);
        const lastMonitoringDate = this.parseBackendLocalDate(sortedMonitorings[sortedMonitorings.length - 1].timestamp);
        const startTime = rangeStart ?? firstMonitoringDate ?? new Date();
        const endTime = rangeEnd ?? lastMonitoringDate ?? startTime;
        const dataMap = new Map<number, any>();
        sortedMonitorings.forEach(monitoring => {
            const timeInSeconds = Math.floor((this.getBackendLocalTimestampMs(monitoring.timestamp) - startTime.getTime()) / 1000);
            dataMap.set(timeInSeconds, monitoring);
        });
        const totalSeconds = Math.floor((endTime.getTime() - startTime.getTime()) / 1000) + 1;
        // Downsampling pour rester sous une limite de points
        const MAX_POINTS = 5000;
        const step = Math.max(1, Math.ceil(totalSeconds / MAX_POINTS));
        const bucketCount = Math.ceil(totalSeconds / step);
        const timestamps: string[] = [];
        const values: number[] = [];
        const monitoringMap = new Map<number, any>();
        for (let b = 0; b < bucketCount; b++) {
            const baseSec = b * step;
            const currentTime = new Date(startTime.getTime() + (baseSec * 1000));
            const utcTimeString = currentTime.getHours().toString().padStart(2, '0') + ':' +
                                 currentTime.getMinutes().toString().padStart(2, '0') + ':' +
                                 currentTime.getSeconds().toString().padStart(2, '0');
            timestamps.push(utcTimeString);

            // Pour chaque bucket, on garde la valeur max (pic) et le monitoring associé
            let bestVal = 0;
            let bestMonitoring: any = null;
            const endSec = Math.min(baseSec + step, totalSeconds);
            for (let i = baseSec; i < endSec; i++) {
                if (dataMap.has(i)) {
                    const m = dataMap.get(i);
                    const v = m.vectorSum || 0;
                    if (Math.abs(v) > Math.abs(bestVal) || bestMonitoring === null) {
                        bestVal = v;
                        bestMonitoring = m;
                    }
                }
            }
            if (bestMonitoring) {
                values.push(bestVal);
                monitoringMap.set(b, bestMonitoring);
            } else {
                values.push(0);
                monitoringMap.set(b, {
                    timestamp: currentTime,
                    vectorSum: 0,
                    hasData: false
                });
            }
        }
        
        return { timestamps, values, monitoringMap };
    }

    private drawingChannel(data: DevicesDto, index: number, serialNumber?: string) {
        if (this.charts[index] && this.chartInitialized[index]) {
            const chartCanvas = this.charts[index].canvas;
            const domCanvas = this.el.nativeElement.querySelector('#canvas-' + index);
            
            if (chartCanvas && document.body.contains(chartCanvas) && chartCanvas === domCanvas) {
                this.updateChartWithScrolling(data, index);
                return;
            } else {
                console.warn(`Chart for device ${index} exists but canvas mismatch or detached. Destroying and recreating.`);
                this.charts[index].destroy();
                delete this.charts[index];
                this.chartInitialized[index] = false;
            }
        }
        
        let canvas = <HTMLCanvasElement>this.el.nativeElement.querySelector('#canvas-' + index);
        if (!canvas) {
            console.warn(`Canvas not found for index ${index}`);
            return;
        }
        let ctx = canvas.getContext('2d');
                if (!ctx) {
            console.warn(`Canvas context not available for index ${index}`);
            return;
        }

        if (this.charts[index]) {
            this.charts[index].destroy();
            delete this.charts[index];
        }
        const { startDate: rangeStart, endDate: rangeEnd } = this.calculateTimeRange(this.selectedTimeRange);
        const hasData = data.monitorings != null && data.monitorings.length > 0;
        const sortedMonitorings = hasData
            ? data.monitorings.sort((a, b) => this.getBackendLocalTimestampMs(a.timestamp) - this.getBackendLocalTimestampMs(b.timestamp))
            : [];
        const { timestamps, values, monitoringMap } = this.createContinuousTimeSeries(sortedMonitorings, rangeStart, rangeEnd);
        const displayTimestamps = timestamps;
        const displayValues = values;
        const yMin = 0;
        const rawMax = hasData ? Math.max(...displayValues) : 0;
        const yMax = hasData ? Math.max(rawMax, 1) : 150;
        const rectangles: { xStart: number, xEnd: number, yTop: number, yBottom: number, value: number, index: number, hasData: boolean, monitoring?: any, serialNumber?: string }[] = [];
        this.charts[index] = new Chart(ctx, {
            type: 'line',
            data: {
                labels: displayTimestamps,
                datasets: [{
                    label: 'Channel #' + (index + 1),
                    data: displayValues,
                    backgroundColor: 'rgba(153, 0, 77, 0.2)',
                    borderColor: 'rgb(153, 0, 77)',
                    borderWidth: 1,
                    pointRadius: 0,
                    pointHoverRadius: 0
                }]
            },
            options: {
                elements: {
                    point: {
                        radius: 0,
                        hitRadius: 0
                    },
                    line: {
                        tension: 0
                    }
                },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false,
                        position: 'bottom',
                        labels: {
                            boxWidth: 0
                        }
                    }
                },
                scales: {
                    x: {  
                        grid: {
                            tickLength: 3,
                        },
                        ticks: {
                            maxTicksLimit: timestamps.length,
                            padding: 0
                        },
                        display: true,
                        title: {
                            display: true,
                            text: 'Time'
                        }
                    },
                    y: {
                        min: yMin,
                        max: yMax,
                        ticks: {
                            stepSize: hasData ? 0.5 : 50
                        },
                        grid: {
                            tickLength: 3
                        }
                    }
                  },
                animation: {
                    duration: 0 // Désactiver les animations pour un défilement fluide
                },
                interaction: {
                    intersect: false
                }
            },
            plugins: [
                {
                    id: 'customFrame',
                    afterDraw: (chart) => {
                        const ctx = chart.ctx;
                        const dataset = chart.data.datasets[0].data as number[];
                        const xScale = chart.scales['x'];
                        const yScale = chart.scales['y'];
                        const chartWidth = chart.chartArea.width;
                        const chartHeight = chart.chartArea.height;
        
                        ctx.save();
        
                        rectangles.length = 0;
        
                        for (let i = 0; i < dataset.length; i++) {
                            const value = dataset[i] as number;
                            const x = xScale.getPixelForValue(i);
                            const y = yScale.getPixelForValue(value);
        
                            const rectWidth = Math.min(50, chartWidth);
                            const rectHeight = chartHeight;
                            const xCenter = x;
        
                            const xStart = Math.max(chart.chartArea.left, xCenter - rectWidth / 2);
                            const adjustedRectWidth = Math.min(rectWidth, chartWidth - (xCenter - chart.chartArea.left));
                            const bandHeight = 10;
                            const radius = 5;
        
                            if (
                                (data.redAlertValue && value > data.redAlertValue) ||
                                (data.orangeAlertValue && value > data.orangeAlertValue) ||
                                (data.yellowAlertValue && value > data.yellowAlertValue)
                            ) {
                                const monitoring = monitoringMap.get(i);
                                rectangles.push({
                                    xStart,
                                    xEnd: xStart + adjustedRectWidth,
                                    yTop: chart.chartArea.top - bandHeight,
                                    yBottom: chart.chartArea.top + rectHeight,
                                    value,
                                    index: i,
                                    hasData: monitoring ? monitoring.hasData !== false : true,
                                    monitoring,
                                    serialNumber
                                });
                            }
                            if (data.redAlertValue && value > data.redAlertValue) {
                                ctx.fillStyle = 'rgba(255, 0, 0, 0.08)';
                                ctx.fillRect(xStart, chart.chartArea.top, adjustedRectWidth, rectHeight);
                                ctx.strokeStyle = 'red';
                                ctx.lineWidth = 2;
                                ctx.strokeRect(xStart, chart.chartArea.top, adjustedRectWidth, rectHeight);
                                ctx.fillStyle = 'rgba(255, 0, 0, 1)';
                                ctx.beginPath();
                                ctx.moveTo(xStart + radius, chart.chartArea.top - bandHeight);
                                ctx.lineTo(xStart + adjustedRectWidth - radius, chart.chartArea.top - bandHeight);
                                ctx.arcTo(xStart + adjustedRectWidth, chart.chartArea.top - bandHeight, xStart + adjustedRectWidth, chart.chartArea.top - radius, radius);
                                ctx.lineTo(xStart + adjustedRectWidth, chart.chartArea.top);
                                ctx.lineTo(xStart, chart.chartArea.top);
                                ctx.arcTo(xStart, chart.chartArea.top - bandHeight, xStart + radius, chart.chartArea.top - bandHeight, radius);
                                ctx.closePath();
                                ctx.fill();
                            } else if (data.orangeAlertValue && value > data.orangeAlertValue) {
                                ctx.fillStyle = 'rgba(255, 165, 0, 0.2)';
                                ctx.fillRect(xStart, chart.chartArea.top, adjustedRectWidth, rectHeight);
                                ctx.strokeStyle = 'orange';
                                ctx.lineWidth = 2;
                                ctx.strokeRect(xStart, chart.chartArea.top, adjustedRectWidth, rectHeight);
                                ctx.fillStyle = 'rgba(255, 165, 0, 1)';
                                ctx.beginPath();
                                ctx.moveTo(xStart + radius, chart.chartArea.top - bandHeight);
                                ctx.lineTo(xStart + adjustedRectWidth - radius, chart.chartArea.top - bandHeight);
                                ctx.arcTo(xStart + adjustedRectWidth, chart.chartArea.top - bandHeight, xStart + adjustedRectWidth, chart.chartArea.top - radius, radius);
                                ctx.lineTo(xStart + adjustedRectWidth, chart.chartArea.top);
                                ctx.lineTo(xStart, chart.chartArea.top);
                                ctx.arcTo(xStart, chart.chartArea.top - bandHeight, xStart + radius, chart.chartArea.top - bandHeight, radius);
                                ctx.closePath();
                                ctx.fill();
                            } else if (data.yellowAlertValue && value > data.yellowAlertValue) {
                                ctx.fillStyle = 'rgba(255, 255, 0, 0.2)';
                                ctx.fillRect(xStart, chart.chartArea.top, adjustedRectWidth, rectHeight);
                                ctx.strokeStyle = 'yellow';
                                ctx.lineWidth = 2;
                                ctx.strokeRect(xStart, chart.chartArea.top, adjustedRectWidth, rectHeight);
                                ctx.fillStyle = 'rgba(255, 255, 0, 1)';
                                ctx.beginPath();
                                ctx.moveTo(xStart + radius, chart.chartArea.top - bandHeight);
                                ctx.lineTo(xStart + adjustedRectWidth - radius, chart.chartArea.top - bandHeight);
                                ctx.arcTo(xStart + adjustedRectWidth, chart.chartArea.top - bandHeight, xStart + adjustedRectWidth, chart.chartArea.top - radius, radius);
                                ctx.lineTo(xStart + adjustedRectWidth, chart.chartArea.top);
                                ctx.lineTo(xStart, chart.chartArea.top);
                                ctx.arcTo(xStart, chart.chartArea.top - bandHeight, xStart + radius, chart.chartArea.top - bandHeight, radius);
                                ctx.closePath();
                                ctx.fill();
                            }
                        }
                        ctx.restore();
                    }
                }
            ]
        });
        // Marquer le graphique comme initialisé
        this.chartInitialized[index] = true;
        canvas.onclick = (event: MouseEvent) => {
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
        
            for (const rectangle of rectangles) {
                this.disabled = true;
                if (
                    x >= rectangle.xStart &&
                    x <= rectangle.xEnd &&
                    y >= rectangle.yTop &&
                    y <= rectangle.yBottom
                ) {
                    if (this.currentRectangleIndex !== rectangle.index) {
                        this.handleRectangleClick(rectangle, data);
                    }
                    break;
                } else {
                    this.removeCurrentPopup();
                }
            }
        };
    }

    private handleRectangleClick(rectangle: { xStart: number, xEnd: number, yTop: number, yBottom: number, value: number, index: number, monitoring?: any, serialNumber?: string }, data: DevicesDto) {
        this.removeCurrentPopup();
    
        const popup = document.createElement('div');
        popup.className = 'monitoring-data-popup';
        popup.style.position = 'fixed';
        popup.style.left = '50%';
        popup.style.top = '50%';
        popup.style.transform = 'translate(-50%, -50%)';
        popup.style.width = 'min(92vw, 34rem)';
        popup.style.maxHeight = '55vh';
        popup.style.overflowY = 'auto';
        popup.style.backgroundColor = 'white';
        popup.style.padding = '10px';
        popup.style.zIndex = '3000';
        popup.style.borderRadius = '8px';
        popup.style.boxShadow = '0 8px 24px rgba(0,0,0,0.25)';
    
        const template = document.getElementById('dataPointPopupTemplate') as HTMLTemplateElement;
        const popupContent = template.content.cloneNode(true) as DocumentFragment;
        popup.appendChild(popupContent);
        document.body.appendChild(popup);
        this.currentPopup = popup;
        this.currentRectangleIndex = rectangle.index;
    
        const timestampElement = popup.querySelector('#timestamp') as HTMLDivElement;
        const ppvMaxElement = popup.querySelector('#ppvMax') as HTMLDivElement;
        const airOverpressurePaElement = popup.querySelector('#airOverpressurePa') as HTMLDivElement;
        const vecSommeElement = popup.querySelector('#vecSomme') as HTMLDivElement;
        const airOverpressureDbElement = popup.querySelector('#airOverpressureDb') as HTMLDivElement;
        const measurementPointElement = popup.querySelector('#measurementPoint') as HTMLDivElement;
        const siteNameElement = popup.querySelector('#siteName') as HTMLDivElement;
        const monitoringPoint = rectangle.monitoring;
        
        if (monitoringPoint) {
            const timestampDate = this.parseBackendLocalDate(monitoringPoint.timestamp);
            const timestamp = timestampDate
                ? timestampDate.toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    timeZoneName: 'short'
                })
                : '--';
            
            timestampElement.textContent = timestamp;
            ppvMaxElement.textContent = `${monitoringPoint.vectorSum ?? '--'} mm/s`;
            vecSommeElement.textContent = monitoringPoint.vectorSum != null ? String(monitoringPoint.vectorSum) : '--';
            airOverpressurePaElement.textContent = 'N/A';
            airOverpressureDbElement.textContent = 'N/A';
            measurementPointElement.textContent = data.deviceName || 'N/A';
            siteNameElement.textContent = this.getSiteName(this.currentSiteId) || 'N/A';
        } else {
            timestampElement.textContent = 'Error loading timestamp';
            ppvMaxElement.textContent = 'Error loading Vector Sum';
            airOverpressurePaElement.textContent = 'N/A';
            measurementPointElement.textContent = 'Error loading Device Name';
            siteNameElement.textContent = 'Error loading Site Name';
            vecSommeElement.textContent = 'N/A';
        }
    
        const viewRecordBtn = popup.querySelector('.view-record-btn') as HTMLButtonElement;
        viewRecordBtn.disabled = true;
        viewRecordBtn.style.opacity = '0.5';
        viewRecordBtn.style.cursor = 'not-allowed';
        
        
        if (rectangle.monitoring.seismographSerialNumber && monitoringPoint && this.currentSiteId) {
            const timestampDate = this.parseBackendLocalDate(monitoringPoint.timestamp);
            if (timestampDate) {
                this.monitoringService.getRecordIdBySerialAndTimestamp(this.currentSiteId, rectangle.monitoring.seismographSerialNumber, timestampDate)
                    .pipe(
                        catchError(error => {
                            console.error('Error fetching record ID:', error);
                            return of(null);
                        })
                    )
                    .subscribe(recordId => {
                        if (recordId !== null && recordId !== 0) {
                            viewRecordBtn.disabled = false;
                            viewRecordBtn.style.opacity = '1';
                            viewRecordBtn.style.cursor = 'pointer';
                            viewRecordBtn.addEventListener('click', () => {
                                const serialNumber = rectangle.monitoring?.seismographSerialNumber;
                                this.router.navigate(['/vibration-detail'], {
                                    queryParams: { recordId: recordId, siteId: this.currentSiteId, deviceId: serialNumber }
                                });
                            });
                        }
                    });
            }
        }
        
        const closeBtn = popup.querySelector('.secondary-button') as HTMLButtonElement;
        closeBtn.addEventListener('click', () => {
            this.removeCurrentPopup();
        });
    }
    pascalToDecibel(pressure: number): number {
        const referencePressure = 20e-6;
        return 20 * Math.log10(pressure / referencePressure);
      }
  getRecordId(device: DevicesDto, index: number): number | null {
    return device.monitorings ? device.monitorings[index].recordId : null;
  }

  viewDeviceDetails(deviceName: string): void {
        this.removeCurrentPopup();
    if (this.currentSiteId) {
        this.router.navigate(['/vibration-detail'], {
            queryParams: { deviceId: deviceName, siteId: this.currentSiteId, date: this.selectedDateString }
        });
    }
}
toggleDetails(index: number): void {
    this.isDetailsVisible[index] = !this.isDetailsVisible[index];

    if (this.isDetailsVisible[index]) {
        const device = this.devices[index];
        if (device) {
            this.deviceDataLengthMap.delete(device.deviceId);
        }
        setTimeout(() => {
            this.loadSignalData(index);
        }, 200);
    } else {
        const subscription = this.pollingSubscriptions.get(index);
        if (subscription) {
            subscription.unsubscribe();
            this.pollingSubscriptions.delete(index);
        }
        if (this.charts[index]) {
            this.charts[index].destroy();
            delete this.charts[index];
        }
        this.chartInitialized[index] = false;
        if (this.isChannelsVisible[index]) {
            this.isChannelsVisible[index] = false;
            this.destroyChannelCharts(index);
        }
    }
}

toggleChannels(index: number): void {
    this.isChannelsVisible[index] = !this.isChannelsVisible[index];

    if (this.isChannelsVisible[index]) {
        setTimeout(() => {
            this.loadChannelData(index);
        }, 200);
    } else {
        this.destroyChannelCharts(index);
    }
}

private destroyChannelCharts(index: number): void {
    if (this.channelCharts[index]) {
        this.channelCharts[index].destroy();
        delete this.channelCharts[index];
    }
}

loadChannelData(index: number): void {
    const device = this.devices[index];
    if (!device) {
        console.warn(`No device found at index ${index}`);
        return;
    }
    const sortedMonitorings = (device.monitorings ?? []).slice().sort((a, b) => 
        this.getBackendLocalTimestampMs(a.timestamp) - this.getBackendLocalTimestampMs(b.timestamp)
    );

    this.drawAllChannelsChart(sortedMonitorings, index);
}

private drawAllChannelsChart(monitorings: any[], deviceIndex: number): void {
    const canvasId = `canvas-channels-${deviceIndex}`;
    let canvas = <HTMLCanvasElement>this.el.nativeElement.querySelector('#' + canvasId);
    
    if (!canvas) {
        console.warn(`Canvas not found for ${canvasId}`);
        return;
    }

    let ctx = canvas.getContext('2d');
    if (!ctx) {
        console.warn(`Canvas context not available for ${canvasId}`);
        return;
    }

    if (this.channelCharts[deviceIndex]) {
        this.channelCharts[deviceIndex].destroy();
        delete this.channelCharts[deviceIndex];
    }
    const { startDate: rangeStart, endDate: rangeEnd } = this.calculateTimeRange(this.selectedTimeRange);
    const totalSeconds = Math.floor((rangeEnd.getTime() - rangeStart.getTime()) / 1000) + 1;
    const dataMap = new Map<number, any>();
    monitorings.forEach(m => {
        const offset = Math.floor((this.getBackendLocalTimestampMs(m.timestamp) - rangeStart.getTime()) / 1000);
        if (offset >= 0 && offset < totalSeconds) dataMap.set(offset, m);
    });
    // Downsampling : limiter le nombre de points pour éviter de figer Chart.js
    // sur de très grandes plages (ex: 02:00 -> 23:59 = ~80k pts × 4 canaux).
    const MAX_POINTS = 5000;
    const step = Math.max(1, Math.ceil(totalSeconds / MAX_POINTS));
    const bucketCount = Math.ceil(totalSeconds / step);
    const allTimestamps: string[] = [];
    for (let b = 0; b < bucketCount; b++) {
        const t = new Date(rangeStart.getTime() + b * step * 1000);
        allTimestamps.push(t.getHours().toString().padStart(2,'0') + ':' + t.getMinutes().toString().padStart(2,'0') + ':' + t.getSeconds().toString().padStart(2,'0'));
    }
    const displayTimestamps = allTimestamps;
    const datasets = [];
    for (let channel = 1; channel <= 4; channel++) {
        const channelKey = `channel${channel}` as keyof typeof monitorings[0];
        const values: number[] = [];
        for (let b = 0; b < bucketCount; b++) {
            // Pour chaque bucket, on prend la valeur max (pic) du segment
            let maxVal = 0;
            const start = b * step;
            const end = Math.min(start + step, totalSeconds);
            for (let i = start; i < end; i++) {
                const m = dataMap.get(i);
                if (m) {
                    const v = (m[channelKey] as number) || 0;
                    if (Math.abs(v) > Math.abs(maxVal)) maxVal = v;
                }
            }
            values.push(maxVal);
        }
        const displayValues = values;

        datasets.push({
            label: this.getLabelForChannel(channel),
            data: displayValues,
            backgroundColor: this.getChannelColor(channel, 0.2),
            borderColor: this.getChannelColor(channel, 1),
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 3,
            fill: false,
            yAxisID: channel === 4 ? 'ySound' : 'y'
        });
    }

    const vibrationValues = datasets.filter(ds => ds.yAxisID !== 'ySound').flatMap(ds => ds.data as number[]);
    const soundValues = datasets.filter(ds => ds.yAxisID === 'ySound').flatMap(ds => ds.data as number[]);
    const minValue = Math.min(...vibrationValues, 0);
    const maxValue = Math.max(...vibrationValues, 1);
    const soundMin = soundValues.length ? Math.min(...soundValues, 0) : 0;
    const soundMax = soundValues.length ? Math.max(...soundValues, 1) : 1;

    this.channelCharts[deviceIndex] = new Chart(ctx, {
        type: 'line',
        data: {
            labels: displayTimestamps,
            datasets: datasets
        },
        options: {
            elements: {
                point: {
                    radius: 0,
                    hitRadius: 5
                },
                line: {
                    tension: 0
                }
            },
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                    labels: {
                        boxWidth: 20,
                        usePointStyle: true
                    },
                    onClick: (e: any, legendItem: any, legend: any) => {
                        const ci = legend.chart;
                        const idx = legendItem.datasetIndex;
                        ci.setDatasetVisibility(idx, !ci.isDatasetVisible(idx));
                        const soundDsIndex = ci.data.datasets.findIndex((d: any) => d.yAxisID === 'ySound');
                        if (ci.options.scales && ci.options.scales['ySound']) {
                            ci.options.scales['ySound'].display = soundDsIndex === -1 ? false : ci.isDatasetVisible(soundDsIndex);
                        }
                        ci.update();
                    }
                },
                tooltip: {
                    mode: 'index',
                    intersect: false
                }
            },
            scales: {
                x: {
                    grid: {
                        tickLength: 3,
                    },
                    ticks: {
                        maxTicksLimit: 10,
                        padding: 0
                    },
                    display: true,
                    title: {
                        display: true,
                        text: 'Time'
                    }
                },
                y: {
                    position: 'left',
                    beginAtZero: true,
                    ticks: {
                        stepSize: 0.5
                    },
                    grid: {
                        tickLength: 3
                    },
                    title: {
                        display: true,
                        text: ''
                    }
                },
                ySound: {
                    position: 'right',
                    min: soundMin,
                    max: soundMax,
                    display: soundValues.length > 0,
                    grid: {
                        drawOnChartArea: false
                    },
                    title: {
                        display: true,
                        text: 'Sound'
                    }
                }
            },
            animation: {
                duration: 0
            },
            interaction: {
                mode: 'nearest',
                axis: 'x',
                intersect: false
            }
        }
    });

}
    private getLabelForChannel(channelNumber: number): string {
        const channelLabels: Record<number, string> = {
            1: 'VIBRATION.VERTICAL',
            2: 'VIBRATION.LONGITUDE',
            3: 'VIBRATION.TRANSVERSE',
            4: 'VIBRATION.SOUND'
        };
        return channelLabels[channelNumber]
            ? this.translateService.instant(channelLabels[channelNumber])
            : `Channel ${channelNumber}`;
    }

    private getChannelColor(channelNumber: number, alpha: number): string {
        const colors = [
            { r: 255, g: 99, b: 132 },
            { r: 54, g: 162, b: 235 },
            { r: 75, g: 192, b: 192 },
            { r: 255, g: 206, b: 86 }
        ];
        const color = colors[channelNumber - 1] || colors[0];
        return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
    }

private updateAllChannelsChart(data: DevicesDto, deviceIndex: number): void {
    if (!data.monitorings || data.monitorings.length === 0) {
        console.warn(`No monitoring data to update channels for device ${deviceIndex}`);
        return;
    }

    const chart = this.channelCharts[deviceIndex];
    if (!chart) {
        console.warn(`Channel chart not found for device ${deviceIndex}`);
        return;
    }

    const canvasId = `canvas-channels-${deviceIndex}`;
    const domCanvas = <HTMLCanvasElement>this.el.nativeElement.querySelector('#' + canvasId);
    
    if (!domCanvas) {
        console.warn(`Canvas ${canvasId} no longer exists in DOM, destroying chart reference`);
        chart.destroy();
        delete this.channelCharts[deviceIndex];
        return;
    }
    
    if (chart.canvas !== domCanvas) {
        console.warn(`Chart canvas mismatch for device ${deviceIndex} (DOM recreated). Destroying old chart.`);
        chart.destroy();
        delete this.channelCharts[deviceIndex];
        return; 
    }

    const sortedMonitorings = data.monitorings.sort((a, b) => 
        this.getBackendLocalTimestampMs(a.timestamp) - this.getBackendLocalTimestampMs(b.timestamp)
    );

    const { startDate: rangeStart, endDate: rangeEnd } = this.calculateTimeRange(this.selectedTimeRange);
    const totalSeconds = Math.floor((rangeEnd.getTime() - rangeStart.getTime()) / 1000) + 1;
    const dataMap = new Map<number, any>();
    sortedMonitorings.forEach(m => {
        const offset = Math.floor((this.getBackendLocalTimestampMs(m.timestamp) - rangeStart.getTime()) / 1000);
        if (offset >= 0 && offset < totalSeconds) dataMap.set(offset, m);
    });
    // Downsampling pour rester sous une limite de points (sinon Chart.js n'affiche rien)
    const MAX_POINTS = 5000;
    const step = Math.max(1, Math.ceil(totalSeconds / MAX_POINTS));
    const bucketCount = Math.ceil(totalSeconds / step);
    const allTimestamps: string[] = [];
    for (let b = 0; b < bucketCount; b++) {
        const t = new Date(rangeStart.getTime() + b * step * 1000);
        allTimestamps.push(t.getHours().toString().padStart(2,'0') + ':' + t.getMinutes().toString().padStart(2,'0') + ':' + t.getSeconds().toString().padStart(2,'0'));
    }
    const displayTimestamps = allTimestamps;

    const allValues: number[] = [];
    const soundValues: number[] = [];
    for (let channel = 1; channel <= 4; channel++) {
        const channelKey = `channel${channel}` as keyof typeof sortedMonitorings[0];
        const values: number[] = [];
        for (let b = 0; b < bucketCount; b++) {
            let maxVal = 0;
            const start = b * step;
            const end = Math.min(start + step, totalSeconds);
            for (let i = start; i < end; i++) {
                const m = dataMap.get(i);
                if (m) {
                    const v = (m[channelKey] as number) || 0;
                    if (Math.abs(v) > Math.abs(maxVal)) maxVal = v;
                }
            }
            values.push(maxVal);
        }
        const displayValues = values;
        chart.data.datasets[channel - 1].data = displayValues as any;
        if (channel === 4) soundValues.push(...displayValues);
        else allValues.push(...displayValues);
    }

    chart.data.labels = displayTimestamps;
    
    const soundMin = soundValues.length ? Math.min(...soundValues, 0) : 0;
    const soundMax = soundValues.length ? Math.max(...soundValues, 1) : 1;
    if (chart.options.scales && chart.options.scales['ySound']) {
        chart.options.scales['ySound'].min = soundMin;
        chart.options.scales['ySound'].max = soundMax;
    }

    chart.update('none');
  }

loadSignalData(index: number): void {
    const device = this.devices[index];
    if (this.pollingSubscriptions.has(index)) {
        this.pollingSubscriptions.get(index)?.unsubscribe();
        this.pollingSubscriptions.delete(index);
    }
    const baseSearchCriteria: any = {
        siteId: this.currentSiteId,
        serialNumber: device.deviceName
      };

    const subscription = interval(5000)
        .pipe(
            startWith(0),
        exhaustMap(() => {
          const { startDate, endDate } = this.calculateTimeRange(this.selectedTimeRange);
          const searchCriteria = {
            ...baseSearchCriteria,
                        startDate: this.formatLocalDateTime(startDate),
                        endDate: this.formatLocalDateTime(endDate)
          };
          return this.monitoringService.getMonitoringDevice(searchCriteria);
        })
        )
        .subscribe({
            next: (response) => {                   
                const deviceId = response.data.deviceId;
                const currentLength = response.data.monitorings?.length ?? 0;
                const storedLength = this.deviceDataLengthMap.get(deviceId);                
                // Update the device data in the array, preserving fields not returned by polling
                this.devices[index] = {
                    ...this.devices[index],
                    ...response.data,
                    siteId: response.data.siteId ?? this.devices[index].siteId,
                    controlPointId: response.data.controlPointId ?? this.devices[index].controlPointId,
                    notes: response.data.notes ?? this.devices[index].notes,
                    modelName: response.data.modelName ?? this.devices[index].modelName,
                    controlPointName: response.data.controlPointName ?? this.devices[index].controlPointName,
                };

                this.computeMaxValues(this.devices[index], index);

                const selectedDateOnly = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate());
                const todayOnly = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate());
                const isToday = selectedDateOnly.getTime() === todayOnly.getTime();

                const shouldUpdate = storedLength === undefined || 
                                   storedLength === 0 || 
                                   (isToday && currentLength > 0) || 
                                   (storedLength !== currentLength) ||
                                   this.chartInitialized[index];

                if (shouldUpdate) {
                    setTimeout(() => {
                      const serialNumber = this.initialDeviceList.find(d => d.deviceId === response.data.deviceId)?.deviceName;
                    if (currentLength === 0) {
                      this.drawingChannel(response.data, index, serialNumber);
                      
                      if (this.isChannelsVisible[index]) {
                        // Recréer un graphe vide aligné sur le range courant
                        this.destroyChannelCharts(index);
                        this.drawAllChannelsChart([], index);
                      }
                    } else {
                      this.drawingChannel(response.data, index, serialNumber);
                      this.checkReachedAlert(response.data);
                      
                      if (this.isChannelsVisible[index]) {
                        const canvasId = `canvas-channels-${index}`;
                        const canvas = <HTMLCanvasElement>this.el.nativeElement.querySelector('#' + canvasId);
                        
                        if (!canvas) {
                          console.warn(`Canvas ${canvasId} not found in DOM yet`);
                        } else {
                          const existingChart = this.channelCharts[index];
                          const isValid = existingChart && existingChart.canvas && document.body.contains(existingChart.canvas) && existingChart.canvas === canvas;

                          if (!isValid) {
                              if (existingChart) {
                                  console.warn(`Chart invalid or mismatch for device ${index}. Recreating.`);
                                  this.destroyChannelCharts(index);
                              } else {
                              }
                              
                              const sortedMonitorings = response.data.monitorings.sort((a: any, b: any) => 
                                  this.getBackendLocalTimestampMs(a.timestamp) - this.getBackendLocalTimestampMs(b.timestamp)
                              );
                              this.drawAllChannelsChart(sortedMonitorings, index);
                          } else {
                              this.updateAllChannelsChart(response.data, index);
                          }
                        }
                      }
                    }
                        this.deviceDataLengthMap.set(deviceId, currentLength);
                    }, 0);
                }
            },
            error: (error) => {
                console.error(`Error loading monitoring data for device index ${index}:`, error);
            }
        });
        this.pollingSubscriptions.set(index, subscription);
    }
    canSeeDetails(device: DevicesDto): boolean {
        return device.monitorings !== null && device.monitorings.length > 0;
    }

    private computeMaxValues(data: DevicesDto, index: number): void {
        const monitorings = data?.monitorings;
        if (!monitorings?.length) {
            this.deviceMaxValues[index] = { maxVecSum: null, maxChannels: null, maxSound: null };
            return;
        }
        let maxVecSum = -Infinity;
        let maxChannels = -Infinity;
        let maxSound = -Infinity;
        for (const m of monitorings) {
            if (m.vectorSum != null) maxVecSum = Math.max(maxVecSum, Math.abs(m.vectorSum));
            const ch123 = Math.max(
                Math.abs(m.channel1 ?? 0),
                Math.abs(m.channel2 ?? 0),
                Math.abs(m.channel3 ?? 0)
            );
            maxChannels = Math.max(maxChannels, ch123);
            if (m.channel4 != null) maxSound = Math.max(maxSound, Math.abs(m.channel4));
        }
        this.deviceMaxValues[index] = {
            maxVecSum: maxVecSum === -Infinity ? null : maxVecSum,
            maxChannels: maxChannels === -Infinity ? null : maxChannels,
            maxSound: maxSound === -Infinity ? null : maxSound
        };
    }

        checkReachedAlert(device: DevicesDto): void {
        const { monitorings, redAlertValue, orangeAlertValue, yellowAlertValue, deviceId } = device;
        if (!monitorings?.length) return;

        const alertLevels = [
            { threshold: redAlertValue, list: this.redAlarmReachedDevices },
            { threshold: orangeAlertValue, list: this.orangeAlarmReachedDevices, upper: redAlertValue },
            { threshold: yellowAlertValue, list: this.yellowAlarmReachedDevices, upper: orangeAlertValue },
        ];

        for (const { threshold, list, upper } of alertLevels) {
            const exceedsThreshold = monitorings.some((monitoring) => {
                const vectorSum = monitoring.vectorSum;
                return vectorSum > threshold && (!upper || vectorSum < upper);
            });

            if (threshold != null && exceedsThreshold && !list.includes(deviceId)) {
                list.push(deviceId);
            }
        }
        }
  onDateChange(event: Event): void {3
  const input = event.target as HTMLInputElement;
  const selectedDate = input.value ? new Date(input.value) : null;
  this.selectedDate = selectedDate;
  if (selectedDate) {
    this.deviceDataLengthMap.clear();
    this.chartInitialized = {};
    this.destroyVisibleChannelCharts();
    this.getDevices();
    this.saveFiltersToStorage();
  }
  }
  
  onStartTimeChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  const v = input.value || '00:00';
  this.startTime = v.length === 5 ? v + ':00' : v;
  this.selectedTimeRange = 'custom';
  if (this.selectedDate) {
    this.deviceDataLengthMap.clear();
    this.chartInitialized = {};
    this.destroyVisibleChannelCharts();
    this.reloadVisibleDeviceData();
    this.saveFiltersToStorage();
  }
  }
  
  onEndTimeChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  const v = input.value || '23:59';
  this.endTime = v.length === 5 ? v + ':59' : v;
  this.selectedTimeRange = 'custom';
  if (this.selectedDate) {
    this.deviceDataLengthMap.clear();
    this.chartInitialized = {};
    this.destroyVisibleChannelCharts();
    this.reloadVisibleDeviceData();
    this.saveFiltersToStorage();
  }
  }

  onTimeInput(event: Event, kind: 'start' | 'end'): void {
    const input = event.target as HTMLInputElement;
    let raw = (input.value || '').replace(/[^\d]/g, '').slice(0, 4);
    // Clamp hours (00-23)
    if (raw.length >= 1) {
      const h0 = parseInt(raw[0], 10);
      if (h0 > 2) raw = '2' + raw.slice(1);
    }
    if (raw.length >= 2) {
      const hh = parseInt(raw.slice(0, 2), 10);
      if (hh > 23) raw = '23' + raw.slice(2);
    }
    // Clamp minutes (00-59)
    if (raw.length >= 3) {
      const m0 = parseInt(raw[2], 10);
      if (m0 > 5) raw = raw.slice(0, 2) + '5' + raw.slice(3);
    }
    let formatted = raw;
    if (raw.length > 2) formatted = raw.slice(0, 2) + ':' + raw.slice(2);
    input.value = formatted;
    if (formatted.length === 5) {
      const suffix = kind === 'start' ? ':00' : ':59';
      if (kind === 'start') this.startTime = formatted + suffix;
      else this.endTime = formatted + suffix;
      this.selectedTimeRange = 'custom';
      if (this.selectedDate) {
        this.deviceDataLengthMap.clear();
        this.chartInitialized = {};
        this.destroyVisibleChannelCharts();
        this.reloadVisibleDeviceData();
        this.saveFiltersToStorage();
      }
    }
  }

  onTimeBlur(kind: 'start' | 'end'): void {
    // Reset to last valid value if input is incomplete
    if (kind === 'start' && (!this.startTime || this.startTime.length < 8)) {
      this.startTime = '00:00:00';
    }
    if (kind === 'end' && (!this.endTime || this.endTime.length < 8)) {
      this.endTime = '23:59:59';
    }
  }

  private destroyVisibleChannelCharts(): void {
    this.isChannelsVisible.forEach((isVisible, index) => {
      if (isVisible) {
        this.destroyChannelCharts(index);
      }
    });
  }
  
  private updateTimeFieldsFromRange(selectedValue: string): void {
    if (selectedValue === 'custom') {
      return;
    }
    
    const { startDate, endDate } = this.calculateTimeRange(selectedValue);
    
    const today = this.getNowInAppTimeZone();
    this.selectedDate = today;
    this.selectedDateString = this.formatDate(today);
    
    this.startTime = this.formatTimeFromDate(startDate);
    this.endTime = this.formatTimeFromDate(endDate);
  }
  
  private formatTimeFromDate(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }
  
  onTimeRangeChange(selectedValue: any): void {
    this.selectedTimeRange = selectedValue;
    this.updateTimeFieldsFromRange(selectedValue);
    
    this.deviceDataLengthMap.clear();
    this.chartInitialized = {};
    this.destroyVisibleChannelCharts();
    this.reloadVisibleDeviceData();
    this.saveFiltersToStorage();
  }
  
  private reloadVisibleDeviceData(): void {
    this.isDetailsVisible.forEach((isVisible, index) => {
    if (isVisible && this.devices[index]) {
            const device = this.devices[index];
            this.deviceDataLengthMap.delete(device.deviceId);
            this.loadSignalData(index);
        }
    });
  }
  
  onDeviceChange(event: Event): void {
    this.deviceDataLengthMap.clear();
    this.currentPage = 1;

    this.pollingSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.pollingSubscriptions.clear();

    Object.keys(this.charts).forEach(key => {
        const index = parseInt(key);
        if (this.charts[index]) {
            this.charts[index].destroy();
            delete this.charts[index];
        }
    });
    this.chartInitialized = {};
    this.getDevices();
    this.saveFiltersToStorage();
  }

  onSiteChange(event: any): void {
        this.selectedSiteId = event?.siteId;
        this.getDevices();
        this.saveFiltersToStorage();
  }
  getSiteName(siteId: number): string {
    const site = this.listeSites.find(s => s.siteId === siteId);
    return site ? site.nom : '';
  }
  getControlPointName(controlPointId: number): string {
    const controlPoint = this.controlPoints.find(s => s.controlPointId === controlPointId);
    return controlPoint ? controlPoint.name : '';
  }

  private saveFiltersToStorage(): void {
    const filters = {
      selectedDeviceIds: this.selectedDeviceIds,
      selectedDate: this.selectedDate?.toISOString(),
      selectedDateString: this.selectedDateString,
      startTime: this.startTime,
      endTime: this.endTime,
      selectedTimeRange: this.selectedTimeRange,
      selectedSiteId: this.selectedSiteId
    };
    this.localStorageService.setItem('monitoringFilters', filters);
  }
  public forceRefreshCharts(): void {
    this.deviceDataLengthMap.clear();
    Object.keys(this.chartInitialized).forEach(key => {
      const index = parseInt(key);
      this.chartInitialized[index] = false;
      if (this.charts[index]) {
        this.charts[index].destroy();
        delete this.charts[index];
      }
    });
    this.reloadVisibleDeviceData();
  }
  private loadSavedFilters(): void {
    const savedFilters: MonitoringFilters = this.localStorageService.getItem('monitoringFilters');
    if (savedFilters) {
      this.selectedDeviceIds = savedFilters.selectedDeviceIds || [];
      this.selectedTimeRange = savedFilters.selectedTimeRange || 'custom';
      this.startTime = savedFilters.startTime || '00:00:00';
      this.endTime = savedFilters.endTime || '23:59:59';
    //   this.selectedSiteId = savedFilters.selectedSiteId || this.currentSiteId;
      if (savedFilters.selectedDate) {
        this.selectedDate = new Date(savedFilters.selectedDate);
        this.selectedDateString = savedFilters.selectedDateString || this.formatDate(this.selectedDate);
      } else {
        this.selectedDate = new Date();
        this.selectedDateString = this.formatDate(new Date());
      }
    } else {
      this.selectedDate = new Date();
      this.selectedDateString = this.formatDate(new Date());
    }
  }
  private updateChartWithScrolling(data: DevicesDto, index: number): void {
    const chart = this.charts[index];
    if (!chart) {
      return;
    }

        const sortedMonitorings = (data.monitorings ?? []).sort((a, b) =>
            this.getBackendLocalTimestampMs(a.timestamp) - this.getBackendLocalTimestampMs(b.timestamp)
        );

    const { startDate: rangeStart, endDate: rangeEnd } = this.calculateTimeRange(this.selectedTimeRange);
    const { timestamps, values } = this.createContinuousTimeSeries(sortedMonitorings, rangeStart, rangeEnd);

    const displayTimestamps = timestamps;
    const displayValues = values;

    chart.data.labels = displayTimestamps;
    chart.data.datasets[0].data = displayValues;

    const minValue = 0;
    const rawMax = displayValues.length ? Math.max(...displayValues) : 0;
    const maxValue = sortedMonitorings.length ? Math.max(rawMax, 1) : 150;
    if (chart.options.scales && chart.options.scales['y']) {
      (chart.options.scales['y'] as any).min = minValue;
      (chart.options.scales['y'] as any).max = maxValue;
    }
    if (chart.options.scales && chart.options.scales['x']) {
      (chart.options.scales['x'] as any).ticks = {
        ...((chart.options.scales['x'] as any).ticks || {}),
        maxTicksLimit: displayTimestamps.length
      };
    }
    chart.update('none');
  }

  ngOnDestroy(): void {
    this.removeCurrentPopup();
    this.pollingSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.pollingSubscriptions.clear();
    Object.keys(this.charts).forEach(key => {
        const index = parseInt(key);
        if (this.charts[index]) {
            this.charts[index].destroy();
            delete this.charts[index];
        }
    });
    this.deviceDataLengthMap.clear();
    this.chartInitialized = {};
}

trackById(index: number, item: DevicesDto): number {
        return item.deviceId;
    }
}